import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseActionTypes } from '../../actions/base.actions';
import { State } from '../../index';
import { getToken } from '../../selectors/base.selectors';
import {
    DeleteDiagnosesicherheitResponse,
    DiagnosesicherheitActionTypes,
    GetDiagnosesicherheitenResponse, PatchDiagnosesicherheitResponse, PostDiagnosesicherheitResponse,
} from '../../actions/mdqx/diagnosesicherheit.actions';
import { stringifyIfNotNullOrUndefined } from '../../../../helper/helper';
import { HttpService } from '../../../services/http.service';
import { DiagnosesicherheitEntity } from '../../../entities/mdqx/diagnosesicherheit.entity';
import { DaveActions } from '../../actions/actions';

enum ErrorCodes {
    Get = 'Diagnosesicherheiten Abrufen fehlgeschlagen',
    Post = 'Diagnosesicherheit Anlegen fehlgeschlagen',
    Patch = 'Diagnosesicherheit Bearbeiten fehlgeschlagen',
    Delete = 'Diagnosesicherheit Löschen fehlgeschlagen',
}

@Injectable()
export class DiagnosesicherheitEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(DiagnosesicherheitActionTypes.GetDiagnosesicherheitenRequest),
        map(
            () => `
            {
                diagnosesicherheit {
                    ${DiagnosesicherheitEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.diagnosesicherheit),
                concatMap(res =>
                    res
                        ? [
                              new GetDiagnosesicherheitenResponse(
                                  res.map(
                                      diagnosesicherheit =>
                                          new DiagnosesicherheitEntity(diagnosesicherheit),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(DiagnosesicherheitActionTypes.PostDiagnosesicherheitRequest),
        map(
            ({ Payload }) => `
            mutation {
                createDiagnosesicherheit(
                    name: ${JSON.stringify(Payload.name)}
                    kuerzel: ${JSON.stringify(Payload.kuerzel)}
                ) {
                    ${DiagnosesicherheitEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.createDiagnosesicherheit),
                concatMap(res =>
                    res
                        ? [new PostDiagnosesicherheitResponse(new DiagnosesicherheitEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));

    
    patch$ = createEffect(() => this.actions$.pipe(
        ofType(DiagnosesicherheitActionTypes.PatchDiagnosesicherheitRequest),
        map(
            ({ Payload }) => `
            mutation {
                changeDiagnosesicherheit(
                    id: ${JSON.stringify(Payload.id)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'kuerzel')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'name')}
                ) {
                    ${DiagnosesicherheitEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }).pipe(
                map(res => res && res.changeDiagnosesicherheit),
                concatMap(res =>
                    res
                        ? [new PatchDiagnosesicherheitResponse(new DiagnosesicherheitEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Patch,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(DiagnosesicherheitActionTypes.DeleteDiagnosesicherheitRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteDiagnosesicherheit(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token },
                )
                .pipe(
                    map(res => res && res.deleteDiagnosesicherheit),
                    concatMap(res =>
                        res
                            ? [new DeleteDiagnosesicherheitResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),
                ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
